import { validate as validateEmail } from 'email-validator';
import { defer, isString } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '../../../data/constants/user';
import { useI18n } from '../../../data/hooks/i18nHooks';
import { usePrefillData } from '../../../data/hooks/sessionHooks';
import APIConnectionWarning from '../../elements/APIConnectionWarning/APIConnectionWarning';
import Buttonlink from '../../elements/ButtonLink';
import { dialogAlert } from '../../elements/Dialog/dialogs';
import Checkbox from '../../elements/forms/Checkbox';
import Input from '../../elements/forms/Input';
import HomeLogo from '../../elements/HomeLogo';
import Link from '../../elements/Link';
import Scrollable from '../../elements/Scrollable';
import TopBar from '../../elements/TopBar/TopBar';
import Screen from '../Screen';
import EmailUsedDialog from './EmailUsedDialog/EmailUsedDialog';
import InfoIcon from './InfoIcon';
import PasswordRequirements from '../MyProfile/ChangeUserPassword/innerComponents/PasswordRequirements/PasswordRequirements';

const Registration = (props) => {
    const {
            onSuccess = () => {
                if (prefill && prefill.code) {
                    app.history.push('/add/' + prefill.code);
                } else {
                    app.history.push('/new_account');
                }
            },
        } = props,
        i18n = useI18n(),
        prefill = usePrefillData(),
        [form, setForm] = useState({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            agree: false,
        }),
        [emailUsedDialog, setEmailUsedDialog] = useState(false),
        [focusEmail, setFocusEmail] = useState(false),
        ref = useRef(),
        [showPasswordHint, setShowPasswordHint] = useState(false);

    const handleTogglePasswordHint = useCallback(() => {
        setShowPasswordHint((showPasswordHint) => !showPasswordHint);
    }, []);

    // useEffect(() => {
    //     console.warn('INSTANCIATING REGISTRATION FORM');
    // }, []);

    useEffect(() => {
        if (focusEmail && ref.current) {
            setTimeout(() => {
                ref.current.querySelector('[name="email"]')?.focus();
                ref.current.querySelector('[name="email"]')?.select();
            }, 500);
            setFocusEmail(false);
        }
    }, [focusEmail]);

    const handleChange = useCallback(
        (field, value) => {
            setForm({
                ...form,
                [field]: value,
            });
        },
        [form]
    );

    const handleSubmit = useCallback(async () => {
        const firstName = form.firstName.trim(),
            lastName = form.lastName.trim(),
            email = form.email.trim(),
            password = form.password.trim(),
            tosAgree = form.agree;

        if (!firstName.length) await dialogAlert(i18n['message.require_firstname']);
        else if (!lastName.length) await dialogAlert(i18n['message.require_lastname']);
        else if (!email.length) await dialogAlert(i18n['message.e-mailfield']);
        else if (!validateEmail(email)) await dialogAlert(i18n['message.e-mailfield']);
        else if (!password || password.length < MIN_PASSWORD_LENGTH) await dialogAlert(i18n['message.passwordlengthexceeds']);
        else if (!tosAgree) await dialogAlert(i18n['message.check_terms']);
        else {
            app.store.dispatch(app.actions.ui.busy());
            app.api.session
                .register(email, password, firstName, lastName)
                .then(function () {
                    onSuccess();
                })
                .catch(async function (catchedError) {
                    let { error, error_details: { has_linkedin, email: emailErrors } = {} } = catchedError.response?.data || {};
                    if (error == 'email already used') {
                        setEmailUsedDialog({ has_linkedin });
                    } else if (error.match(/validation failed/)) {
                        if (emailErrors?.length) {
                            await dialogAlert(i18n['message.e-mailfield']);
                        } else {
                            // other error
                            const firstError = Object.values(catchedError.response?.data?.error_details).find((e) => e.length)?.[0];
                            if (isString(firstError) && firstError.length) {
                                await dialogAlert(firstError);
                            }
                        }
                    } else {
                        await dialogAlert(i18n['message.networkerror']);
                    }
                })
                .then(() => {
                    app.store.dispatch(app.actions.ui.free());
                });
        }
    }, [form, prefill]);

    const handleDifferentEmail = useCallback(() => {
        setEmailUsedDialog(false);
        setFocusEmail(true);
    }, [form]);

    const handleCloseDialog = useCallback(() => {
        setEmailUsedDialog(false);
    }, []);

    const header = <TopBar returnURL="/" />,
        footer = <APIConnectionWarning />;

    return (
        <Screen ref={ref} className="authentication-screen" header={header} footer={footer}>
            <EmailUsedDialog email={form.email} onDifferentEmail={handleDifferentEmail} onClose={handleCloseDialog} show={!!emailUsedDialog} {...emailUsedDialog} />
            <Scrollable>
                <HomeLogo />
                <h1>{i18n['label.new.account']}</h1>
                <div className="content" ref={ref}>
                    <p>{i18n['label.fill.details.below']}</p>
                    <Input name="firstName" label={i18n['label.first.name']} value={form.firstName} onChange={(value) => handleChange('firstName', value)} autoFocus={!focusEmail} />
                    <Input name="lastName" label={i18n['label.lastname']} value={form.lastName} onChange={(value) => handleChange('lastName', value)} />
                    <Input
                        name="email"
                        label={i18n['label.email.address']}
                        value={form.email}
                        minLength={7}
                        maxLength={128}
                        onChange={(value) => handleChange('email', value)}
                        type="email"
                        autoFocus={focusEmail}
                    />
                    <Input
                        name="password"
                        label={
                            <span>
                                {i18n['label.password']} <InfoIcon onClick={handleTogglePasswordHint} />
                            </span>
                        }
                        value={form.password}
                        minLength={MIN_PASSWORD_LENGTH}
                        maxLength={MAX_PASSWORD_LENGTH}
                        onChange={(value) => handleChange('password', value)}
                        type="password"
                    />

                    {showPasswordHint ? <PasswordRequirements className="content indent" /> : null}
                    <br />
                    <Checkbox name="agree" checked={form.agree} onChange={(isChecked) => handleChange('agree', isChecked)}>
                        {i18n['label.agree.terms.privacy.policy']}
                        <br />
                        <Link href="/user_agreement">{i18n['label.user.agreement']}</Link> and <Link href="/privacy_statement">{i18n['label.PrivacyPolicy.title']}</Link>
                    </Checkbox>
                    <br />
                    <Buttonlink type="submit" className="primaryaction" onClick={handleSubmit}>
                        {i18n['label.register']}
                    </Buttonlink>
                </div>
            </Scrollable>
        </Screen>
    );
};

export default Registration;
